/**
 * Internal icon builders — lucide-style glyphs constructed via the DOM (never
 * `innerHTML`, so a strict `style-src`/`script-src` CSP is satisfied). Each returns
 * a fresh `<svg>` that inherits its colour from `currentColor`, so the consuming
 * component tints it with a token. Shared by the intent-carrying primitives
 * (NoticeBanner, Toast) and EmptyState so one glyph vocabulary is drawn everywhere.
 * (Catalog: Internal › Icons — the one glyph vocabulary every component draws from; not an element of its own.)
 *
 * ## Where these paths come from, and under what terms (CAOS-602)
 *
 * No icon package is installed anywhere in this repository and none is shipped. What IS here is
 * path data: many of these glyphs are lucide's own outlines, typed into this file, and the rest are
 * drawn by us in the same idiom so one vocabulary reads as one vocabulary.
 *
 * lucide is published under the **ISC Licence**, `Copyright (c) 2026 Lucide Icons and Contributors`,
 * which carries forward `Copyright (c) 2013-present Cole Bemis` from Feather. ISC permits use and
 * redistribution and requires that the copyright notice and the permission notice appear in all
 * copies — which is why they are recorded here, beside the paths, rather than left to be assumed
 * from the visual resemblance. The full text is at https://github.com/lucide-icons/lucide/LICENSE.
 *
 * Nothing here is taken from the incumbent's icon set. Where our surfaces want a CONCEPT that
 * appears in theirs — a trend, a score, a strength meter — the concept is nobody's property and we
 * draw our own mark for it or take a freely licensed one. It is the artwork that is theirs.
 */
const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * The whole vocabulary, in reading order — ONE list, so the names a caller may pass and the names
 * this file draws cannot drift apart. It is the runtime list too, which is what lets the component
 * library show every glyph instead of a hand-kept sample of them.
 */
export const ICON_NAMES = [
  'user',
  'book-open',
  'pin',
  'pin-off',
  'info',
  'success',
  'warning',
  'danger',
  'close',
  'chevron',
  'more',
  'search',
  'lock',
  'record',
  'trend-up',
  'trend-down',
  'trend-flat',
  'computed',
  'overridden',
  'inherited',
  'synced',
  // Object glyphs — the leading mark a workspace tab carries for the thing it holds. Verbatim
  // lucide paths, the same source the reference design imports, so one icon vocabulary is drawn
  // everywhere rather than each surface inventing its own.
  'building',
  'users',
  'folder',
  'file-text',
  'clipboard',
  'files',
  'user-check',
  // The administration vocabulary (CAOS-324) — the marks the Setup rail leads its entries with.
  'shield-check',
  'layers',
  'layout-grid',
  'layout-template',
  'boxes',
  'inbox',
  'settings',
  'list',
  'share',
  // CAOS-378 — the marks the reference workspace draws for the same things, taken verbatim from the
  // same glyph library it imports so the two are identical rather than merely similar.
  'house',
  'building-2',
  'folder-kanban',
  'clipboard-check',
  'file-clock',
  'user-round',
  'sparkles',
  // The list-view vocabulary — one control per way of showing the same records.
  'table-2',
  'kanban',
  'columns-2',
  'plus',
  'refresh-cw',
  'sliders-horizontal',
  // CAOS-595 — the rich text toolbar: one mark per formatting command.
  'bold',
  'italic',
  'underline',
  'strikethrough',
  'list-ordered',
  'link',
  'remove-formatting',
  // CAOS-602 — the marks components had already drawn for THEMSELVES because this list did not
  // carry them. Each of these existed privately in one or two components before it existed here;
  // `arrow-up-right` had been drawn twice, independently, which is the second icon set starting.
  'check',
  'message-square',
  'arrow-up-right',
  'clock',
  'eye-off',
  // The chevron family. `chevron` has always meant DOWN and still does, so every existing caller is
  // unchanged — but four components wanted another direction, found one mark, and drew their own
  // rather than reasoning about rotation. A direction you can name is a direction you can find.
  'chevron-up',
  'chevron-right',
  'chevron-left',
  // CAOS-602 — the marks the four remaining private sets were drawing for themselves. The top bar
  // built nine of these from markup strings rather than through the DOM, which is the convention
  // this vocabulary exists to hold; the sign-in card built five more, two of them marks this list
  // already carried. Where a private drawing and this list disagreed, the list wins: one mark for
  // one idea is the whole point, and a component that wants a different weight sets one.
  'sun',
  'moon',
  'eye',
  'mail',
  'shield',
  'log-out',
  'square-pen',
  'message-square-text',
  'download',
  'package',
  // CAOS-50 — asking for help is not the same idea as `info`, which offers something already
  // written down. This is the mark for a person with a question and nowhere yet to put it.
  'circle-help',
  // CAOS-378 — the marks the Setup rail needs in order to draw what the reference draws, plus two
  // that authored metadata was ALREADY asking for and this vocabulary did not carry: `paperclip`
  // (Setup › Files) and `flag` (My Feedback). Those two had been naming a glyph that does not
  // exist since they were authored, and nothing went red, because a name the vocabulary does not
  // carry is skipped rather than drawn — a blank leading mark, indistinguishable from a rail entry
  // that never asked for one. Nothing in this repository could have caught it: the scan in
  // `caos-602-one-icon-vocabulary.test.ts` reads the source trees that DRAW, and a package repo's
  // authored metadata is neither of those.
  'workflow',
  'file-cog',
  'bot',
  'plug',
  'database',
  'terminal',
  'bug',
  'scroll-text',
  'paperclip',
  'flag',
] as const;

export type IconName = (typeof ICON_NAMES)[number];

const ICON_NAME_SET: ReadonlySet<string> = new Set(ICON_NAMES);

/** Is `name` a glyph this vocabulary draws? Anything else is skipped rather than drawn blank. */
export function isIconName(name: string): name is IconName {
  return ICON_NAME_SET.has(name);
}

function svgEl(): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  return svg;
}

function child(tag: string, attrs: Record<string, string>): SVGElement {
  const el = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  return el;
}

/** Build one glyph by name. */
export function makeIcon(name: IconName): SVGSVGElement {
  const svg = svgEl();
  switch (name) {
    case 'house': // a house — the mark a Home destination carries, everywhere
      svg.append(
        child('path', { d: 'M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8' }),
        child('path', {
          d: 'M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
        }),
      );
      break;
    case 'building-2': // an office block — an organisation you do business with
      svg.append(
        child('path', { d: 'M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z' }),
        child('path', { d: 'M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2' }),
        child('path', { d: 'M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2' }),
        child('path', { d: 'M10 6h4' }),
        child('path', { d: 'M10 10h4' }),
        child('path', { d: 'M10 14h4' }),
        child('path', { d: 'M10 18h4' }),
      );
      break;
    case 'folder-kanban': // a folder with a board inside — a project, which has work in stages
      svg.append(
        child('path', {
          d: 'M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z',
        }),
        child('path', { d: 'M8 10v4' }),
        child('path', { d: 'M12 10v2' }),
        child('path', { d: 'M16 10v6' }),
      );
      break;
    case 'clipboard-check': // a clipboard with a tick — an order, which is a thing agreed
      svg.append(
        child('rect', { width: '8', height: '4', x: '8', y: '2', rx: '1', ry: '1' }),
        child('path', { d: 'M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2' }),
        child('path', { d: 'm9 14 2 2 4-4' }),
      );
      break;
    case 'file-clock': // a document with a clock — a change to something already agreed
      svg.append(
        child('path', { d: 'M16 22h2a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v3' }),
        child('path', { d: 'M14 2v4a2 2 0 0 0 2 2h4' }),
        child('circle', { cx: '8', cy: '16', r: '6' }),
        child('path', { d: 'M9.5 17.5 8 16.25V14' }),
      );
      break;
    case 'user-round': // one person, rounded — a person’s own account
      svg.append(
        child('circle', { cx: '12', cy: '8', r: '5' }),
        child('path', { d: 'M20 21a8 8 0 0 0-16 0' }),
      );
      break;
    case 'sparkles': // sparkles — the mark the assistant carries
      svg.append(
        child('path', {
          d: 'M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z',
        }),
        child('path', { d: 'M20 3v4' }),
        child('path', { d: 'M22 5h-4' }),
        child('path', { d: 'M4 17v2' }),
        child('path', { d: 'M5 18H3' }),
      );
      break;
    case 'table-2': // a grid of rows and columns — the table view of a list
      svg.append(
        child('path', {
          d: 'M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18',
        }),
      );
      break;
    case 'kanban': // three columns of differing height — the board view of a list
      svg.append(
        child('path', { d: 'M6 5v11' }),
        child('path', { d: 'M12 5v6' }),
        child('path', { d: 'M18 5v14' }),
      );
      break;
    case 'columns-2': // a pane split in two — the split view of a list
      svg.append(
        child('rect', { width: '18', height: '18', x: '3', y: '3', rx: '2' }),
        child('path', { d: 'M12 3v18' }),
      );
      break;
    case 'plus': // a plus — make a new one
      svg.append(
        child('path', { d: 'M5 12h14' }),
        child('path', { d: 'M12 5v14' }),
      );
      break;
    case 'refresh-cw': // a circling arrow — fetch this again
      svg.append(
        child('path', { d: 'M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8' }),
        child('path', { d: 'M21 3v5h-5' }),
        child('path', { d: 'M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16' }),
        child('path', { d: 'M8 16H3v5' }),
      );
      break;
    case 'sliders-horizontal': // three sliders — narrow this down
      svg.append(
        child('line', { x1: '21', x2: '14', y1: '4', y2: '4' }),
        child('line', { x1: '10', x2: '3', y1: '4', y2: '4' }),
        child('line', { x1: '21', x2: '12', y1: '12', y2: '12' }),
        child('line', { x1: '8', x2: '3', y1: '12', y2: '12' }),
        child('line', { x1: '21', x2: '16', y1: '20', y2: '20' }),
        child('line', { x1: '12', x2: '3', y1: '20', y2: '20' }),
        child('line', { x1: '14', x2: '14', y1: '2', y2: '6' }),
        child('line', { x1: '8', x2: '8', y1: '10', y2: '14' }),
        child('line', { x1: '16', x2: '16', y1: '18', y2: '22' }),
      );
      break;
    case 'book-open': // an open book — the guide's own mark
      svg.append(
        child('path', { d: 'M12 7v14' }),
        child('path', {
          d: 'M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z',
        }),
      );
      break;
    case 'pin': // a thumbtack, pushed in
      svg.append(
        child('path', { d: 'M12 17v5' }),
        child('path', {
          d: 'M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z',
        }),
      );
      break;
    case 'pin-off': // the same thumbtack, struck through — not pushed in
      svg.append(
        child('path', { d: 'M12 17v5' }),
        child('path', { d: 'M15 9.34V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H7.89' }),
        child('path', { d: 'm2 2 20 20' }),
        child('path', { d: 'M9 9v1.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h11' }),
      );
      break;
    case 'user': // one person — head + shoulders
      svg.append(
        child('path', { d: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2' }),
        child('circle', { cx: '12', cy: '7', r: '4' }),
      );
      break;
    case 'circle-help': // circle + the hook and dot of a question mark
      svg.append(
        child('circle', { cx: '12', cy: '12', r: '10' }),
        child('path', { d: 'M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3' }),
        child('line', { x1: '12', y1: '17', x2: '12.01', y2: '17' }),
      );
      break;
    case 'info': // circle + stem + dot
      svg.append(
        child('circle', { cx: '12', cy: '12', r: '10' }),
        child('line', { x1: '12', y1: '16', x2: '12', y2: '12' }),
        child('line', { x1: '12', y1: '8', x2: '12.01', y2: '8' }),
      );
      break;
    case 'success': // circle + check
      svg.append(
        child('path', { d: 'M22 11.08V12a10 10 0 1 1-5.93-9.14' }),
        child('polyline', { points: '22 4 12 14.01 9 11.01' }),
      );
      break;
    case 'warning': // triangle + bang
      svg.append(
        child('path', { d: 'M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' }),
        child('line', { x1: '12', y1: '9', x2: '12', y2: '13' }),
        child('line', { x1: '12', y1: '17', x2: '12.01', y2: '17' }),
      );
      break;
    case 'danger': // circle + x
      svg.append(
        child('circle', { cx: '12', cy: '12', r: '10' }),
        child('line', { x1: '15', y1: '9', x2: '9', y2: '15' }),
        child('line', { x1: '9', y1: '9', x2: '15', y2: '15' }),
      );
      break;
    case 'close': // x
      svg.append(
        child('line', { x1: '18', y1: '6', x2: '6', y2: '18' }),
        child('line', { x1: '6', y1: '6', x2: '18', y2: '18' }),
      );
      break;
    case 'chevron': // down chevron (rotated by the consumer for expand/collapse)
      svg.append(child('polyline', { points: '6 9 12 15 18 9' }));
      break;
    case 'more': // three horizontal dots — overflow trigger
      svg.append(
        child('circle', { cx: '5', cy: '12', r: '1' }),
        child('circle', { cx: '12', cy: '12', r: '1' }),
        child('circle', { cx: '19', cy: '12', r: '1' }),
      );
      break;
    case 'search': // magnifier — typeahead / lookup affordance
      svg.append(
        child('circle', { cx: '11', cy: '11', r: '8' }),
        child('line', { x1: '21', y1: '21', x2: '16.65', y2: '16.65' }),
      );
      break;
    case 'lock': // padlock — read-only marker
      svg.append(
        child('rect', { x: '3', y: '11', width: '18', height: '11', rx: '2', ry: '2' }),
        child('path', { d: 'M7 11V7a5 5 0 0 1 10 0v4' }),
      );
      break;
    case 'record': // document — generic record glyph
      svg.append(
        child('path', { d: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' }),
        child('polyline', { points: '14 2 14 8 20 8' }),
        child('line', { x1: '8', y1: '13', x2: '16', y2: '13' }),
        child('line', { x1: '8', y1: '17', x2: '13', y2: '17' }),
      );
      break;
    case 'trend-up': // up-right arrow
      svg.append(
        child('polyline', { points: '23 6 13.5 15.5 8.5 10.5 1 18' }),
        child('polyline', { points: '17 6 23 6 23 12' }),
      );
      break;
    case 'trend-down': // down-right arrow
      svg.append(
        child('polyline', { points: '23 18 13.5 8.5 8.5 13.5 1 6' }),
        child('polyline', { points: '17 18 23 18 23 12' }),
      );
      break;
    case 'trend-flat': // horizontal arrow
      svg.append(
        child('line', { x1: '2', y1: '12', x2: '20', y2: '12' }),
        child('polyline', { points: '15 7 20 12 15 17' }),
      );
      break;
    case 'computed': // function / calculated — sigma-like brackets + equals
      svg.append(
        child('line', { x1: '5', y1: '9', x2: '5', y2: '5' }),
        child('path', { d: 'M5 5h6M5 19h6' }),
        child('line', { x1: '5', y1: '19', x2: '5', y2: '15' }),
        child('line', { x1: '19', y1: '10', x2: '19', y2: '14' }),
        child('line', { x1: '13', y1: '12', x2: '19', y2: '12' }),
      );
      break;
    case 'overridden': // pin — a human-pinned value over a calculated one
      svg.append(
        child('line', { x1: '12', y1: '17', x2: '12', y2: '22' }),
        child('path', { d: 'M9 2h6l-1 7 3 3v2H7v-2l3-3z' }),
      );
      break;
    case 'inherited': // corner-down-right — flows from a parent/default
      svg.append(
        child('polyline', { points: '15 10 20 15 15 20' }),
        child('path', { d: 'M4 4v7a4 4 0 0 0 4 4h12' }),
      );
      break;
    case 'synced': // refresh — synced from an external system
      svg.append(
        child('polyline', { points: '23 4 23 10 17 10' }),
        child('polyline', { points: '1 20 1 14 7 14' }),
        child('path', { d: 'M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15' }),
      );
      break;
    case 'building': // lucide `building-2` — an account / organization
      svg.append(
        child('path', { d: 'M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z' }),
        child('path', { d: 'M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2' }),
        child('path', { d: 'M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2' }),
        child('path', { d: 'M10 6h4M10 10h4M10 14h4M10 18h4' }),
      );
      break;
    case 'users': // lucide `users` — people / contacts
      svg.append(
        child('path', { d: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' }),
        child('circle', { cx: '9', cy: '7', r: '4' }),
        child('path', { d: 'M22 21v-2a4 4 0 0 0-3-3.87' }),
        child('path', { d: 'M16 3.13a4 4 0 0 1 0 7.75' }),
      );
      break;
    case 'folder': // lucide `folder-kanban` — a project
      svg.append(
        child('path', { d: 'M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z' }),
        child('path', { d: 'M8 10v4M12 10v2M16 10v6' }),
      );
      break;
    case 'file-text': // lucide `file-text` — a document-shaped record (an invoice, a contract)
      svg.append(
        child('path', { d: 'M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z' }),
        child('path', { d: 'M14 2v4a2 2 0 0 0 2 2h4' }),
        child('path', { d: 'M10 9H8M16 13H8M16 17H8' }),
      );
      break;
    case 'clipboard': // lucide `clipboard-list` — an order
      svg.append(
        child('rect', { x: '8', y: '2', width: '8', height: '4', rx: '1' }),
        child('path', { d: 'M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2' }),
        child('path', { d: 'M12 11h4M12 16h4M8 11h.01M8 16h.01' }),
      );
      break;
    case 'files': // lucide `file-stack` — a revised document
      svg.append(
        child('path', { d: 'M21 7h-3a2 2 0 0 1-2-2V2' }),
        child('path', { d: 'M21 6v6a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h4Z' }),
        child('path', { d: 'M7 8v12a2 2 0 0 0 2 2h8' }),
        child('path', { d: 'M3 12v8a2 2 0 0 0 2 2h8' }),
      );
      break;
    case 'user-check': // lucide `user-check` — an approval
      svg.append(
        child('path', { d: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' }),
        child('circle', { cx: '9', cy: '7', r: '4' }),
        child('polyline', { points: '16 11 18 13 22 9' }),
      );
      break;
    case 'shield-check': // lucide `shield-check` — a grant of access
      svg.append(
        child('path', {
          d: 'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z',
        }),
        child('path', { d: 'm9 12 2 2 4-4' }),
      );
      break;
    case 'layers': // lucide `layers` — grants bundled together
      svg.append(
        child('path', {
          d: 'm12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z',
        }),
        child('path', { d: 'm22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65' }),
        child('path', { d: 'm22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65' }),
      );
      break;
    case 'layout-grid': // lucide `layout-grid` — the apps
      svg.append(
        child('rect', { width: '7', height: '7', x: '3', y: '3', rx: '1' }),
        child('rect', { width: '7', height: '7', x: '14', y: '3', rx: '1' }),
        child('rect', { width: '7', height: '7', x: '14', y: '14', rx: '1' }),
        child('rect', { width: '7', height: '7', x: '3', y: '14', rx: '1' }),
      );
      break;
    case 'layout-template': // lucide `layout-template` — a page layout
      svg.append(
        child('rect', { width: '18', height: '7', x: '3', y: '3', rx: '1' }),
        child('rect', { width: '9', height: '7', x: '3', y: '14', rx: '1' }),
        child('rect', { width: '5', height: '7', x: '16', y: '14', rx: '1' }),
      );
      break;
    case 'boxes': // lucide `boxes` — the objects
      svg.append(
        child('path', {
          d: 'M2.97 12.92A2 2 0 0 0 2 14.63v3.24a2 2 0 0 0 .97 1.71l3 1.8a2 2 0 0 0 2.06 0L12 19v-5.5l-5-3-4.03 2.42Z',
        }),
        child('path', { d: 'm7 16.5-4.74-2.85' }),
        child('path', { d: 'm7 16.5 5-3' }),
        child('path', { d: 'M7 16.5v5.17' }),
        child('path', {
          d: 'M12 13.5V19l3.97 2.38a2 2 0 0 0 2.06 0l3-1.8a2 2 0 0 0 .97-1.71v-3.24a2 2 0 0 0-.97-1.71L17 10.5l-5 3Z',
        }),
        child('path', { d: 'm17 16.5-5-3' }),
        child('path', { d: 'm17 16.5 4.74-2.85' }),
        child('path', { d: 'M17 16.5v5.17' }),
        child('path', {
          d: 'M7.97 4.42A2 2 0 0 0 7 6.13v4.37l5 3 5-3V6.13a2 2 0 0 0-.97-1.71l-3-1.8a2 2 0 0 0-2.06 0l-3 1.8Z',
        }),
        child('path', { d: 'M12 8 7.26 5.15' }),
        child('path', { d: 'm12 8 4.74-2.85' }),
        child('path', { d: 'M12 13.5V8' }),
      );
      break;
    case 'inbox': // lucide `inbox` — what has come in
      svg.append(
        child('polyline', { points: '22 12 16 12 14 15 10 15 8 12 2 12' }),
        child('path', {
          d: 'M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z',
        }),
      );
      break;
    case 'settings': // lucide `settings` — administration itself
      svg.append(
        child('path', {
          d: 'M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z',
        }),
        child('circle', { cx: '12', cy: '12', r: '3' }),
      );
      break;
    case 'list': // lucide `list` — an enumerated set of values
      svg.append(
        child('path', { d: 'M3 12h.01' }),
        child('path', { d: 'M3 18h.01' }),
        child('path', { d: 'M3 6h.01' }),
        child('path', { d: 'M8 12h13' }),
        child('path', { d: 'M8 18h13' }),
        child('path', { d: 'M8 6h13' }),
      );
      break;
    case 'share': // lucide `share-2` — who a record reaches
      svg.append(
        child('circle', { cx: '18', cy: '5', r: '3' }),
        child('circle', { cx: '6', cy: '12', r: '3' }),
        child('circle', { cx: '18', cy: '19', r: '3' }),
        child('line', { x1: '8.59', x2: '15.42', y1: '13.51', y2: '17.49' }),
        child('line', { x1: '15.41', x2: '8.59', y1: '6.51', y2: '10.49' }),
      );
      break;
    case 'bold': // lucide `bold` — strong emphasis
      svg.append(child('path', { d: 'M6 12h9a4 4 0 0 1 0 8H7a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h7a4 4 0 0 1 0 8' }));
      break;
    case 'italic': // lucide `italic` — emphasis
      svg.append(
        child('line', { x1: '19', x2: '10', y1: '4', y2: '4' }),
        child('line', { x1: '14', x2: '5', y1: '20', y2: '20' }),
        child('line', { x1: '15', x2: '9', y1: '4', y2: '20' }),
      );
      break;
    case 'underline': // lucide `underline`
      svg.append(
        child('path', { d: 'M6 4v6a6 6 0 0 0 12 0V4' }),
        child('line', { x1: '4', x2: '20', y1: '20', y2: '20' }),
      );
      break;
    case 'strikethrough': // lucide `strikethrough`
      svg.append(
        child('path', { d: 'M16 4H9a3 3 0 0 0-2.83 4' }),
        child('path', { d: 'M14 12a4 4 0 0 1 0 8H6' }),
        child('line', { x1: '4', x2: '20', y1: '12', y2: '12' }),
      );
      break;
    case 'list-ordered': // lucide `list-ordered` — a numbered list
      svg.append(
        child('path', { d: 'M10 12h11' }),
        child('path', { d: 'M10 18h11' }),
        child('path', { d: 'M10 6h11' }),
        child('path', { d: 'M4 10h2' }),
        child('path', { d: 'M4 6h1v4' }),
        child('path', { d: 'M6 18H4c0-1 2-2 2-3s-1-1.5-2-1' }),
      );
      break;
    case 'link': // lucide `link` — a web or email address
      svg.append(
        child('path', { d: 'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71' }),
        child('path', { d: 'M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71' }),
      );
      break;
    case 'remove-formatting': // lucide `remove-formatting` — back to plain text
      svg.append(
        child('path', { d: 'M4 7V4h16v3' }),
        child('path', { d: 'M5 20h6' }),
        child('path', { d: 'M13 4 8 20' }),
        child('path', { d: 'm15 15 5 5' }),
        child('path', { d: 'm20 15-5 5' }),
      );
      break;

    // ── CAOS-602 — the marks components had drawn privately ──────────────────────────────────
    case 'check': // a tick — a thing is chosen, or done
      // Drawn at the vocabulary's own stroke width. The checkbox that owned this privately used a
      // heavier stroke; a component that wants a bolder tick sets `stroke-width` on what it gets
      // back, which is a presentation decision and not a second glyph.
      svg.append(child('polyline', { points: '20 6 9 17 4 12' }));
      break;
    case 'message-square': // a speech bubble — say something, or open a conversation
      svg.append(child('path', { d: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' }));
      break;
    case 'arrow-up-right': // leaves this surface for another one — a deep link
      svg.append(
        child('line', { x1: '7', y1: '17', x2: '17', y2: '7' }),
        child('polyline', { points: '7 7 17 7 17 17' }),
      );
      break;
    case 'clock': // when, or how long — a time-bearing thing
      svg.append(
        child('circle', { cx: '12', cy: '12', r: '10' }),
        child('polyline', { points: '12 6 12 12 16 14' }),
      );
      break;
    case 'eye-off': // hidden, or hide this
      svg.append(
        child('path', { d: 'M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a13.16 13.16 0 0 1-1.67 2.68' }),
        child('path', { d: 'M6.61 6.61A13.526 13.526 0 0 0 2 12s3 8 10 8a9.74 9.74 0 0 0 5.39-1.61' }),
        child('line', { x1: '2', y1: '2', x2: '22', y2: '22' }),
      );
      break;
    case 'chevron-up': // collapse, or sort ascending
      svg.append(child('polyline', { points: '18 15 12 9 6 15' }));
      break;
    case 'chevron-right': // go forward, or step into — the separator a trail draws
      svg.append(child('polyline', { points: '9 6 15 12 9 18' }));
      break;
    case 'chevron-left': // go back
      svg.append(child('polyline', { points: '15 6 9 12 15 18' }));
      break;

    // ── CAOS-602 — the marks the top bar, the sign-in card, the impersonation strip and the
    // empty workspace had each drawn for themselves ─────────────────────────────────────────────
    case 'sun': // the light theme
      svg.append(
        child('circle', { cx: '12', cy: '12', r: '4' }),
        child('path', { d: 'M12 2v2' }),
        child('path', { d: 'M12 20v2' }),
        child('path', { d: 'm4.93 4.93 1.41 1.41' }),
        child('path', { d: 'm17.66 17.66 1.41 1.41' }),
        child('path', { d: 'M2 12h2' }),
        child('path', { d: 'M20 12h2' }),
        child('path', { d: 'm6.34 17.66-1.41 1.41' }),
        child('path', { d: 'm19.07 4.93-1.41 1.41' }),
      );
      break;
    case 'moon': // the dark theme
      svg.append(child('path', { d: 'M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z' }));
      break;
    case 'eye': // shown, or show this — the counterpart of eye-off
      svg.append(
        child('path', { d: 'M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z' }),
        child('circle', { cx: '12', cy: '12', r: '3' }),
      );
      break;
    case 'mail': // an email address
      svg.append(
        child('rect', { x: '2', y: '4', width: '20', height: '16', rx: '2' }),
        child('path', { d: 'm22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7' }),
      );
      break;
    case 'shield': // sign in through a provider that vouches for you
      svg.append(
        child('path', {
          d: 'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z',
        }),
      );
      break;
    case 'log-out': // leave — a door with an arrow going out of it
      svg.append(
        child('path', { d: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4' }),
        child('polyline', { points: '16 17 21 12 16 7' }),
        child('line', { x1: '21', y1: '12', x2: '9', y2: '12' }),
      );
      break;
    case 'square-pen': // edit the thing you are looking at
      svg.append(
        child('path', { d: 'M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' }),
        child('path', { d: 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z' }),
      );
      break;
    case 'message-square-text': // a conversation that already has something in it
      svg.append(
        child('path', { d: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' }),
        child('path', { d: 'M13 8H7' }),
        child('path', { d: 'M17 12H7' }),
      );
      break;
    case 'download': // bring something in — an arrow down into a tray
      svg.append(
        child('path', { d: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' }),
        child('path', { d: 'M7 10l5 5 5-5' }),
        child('path', { d: 'M12 15V3' }),
      );
      break;
    case 'package': // a package — something installable, closed and shippable
      svg.append(
        child('path', { d: 'm7.5 4.27 9 5.15' }),
        child('path', {
          d: 'M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z',
        }),
        child('path', { d: 'm3.3 7 8.7 5 8.7-5' }),
        child('path', { d: 'M12 22V12' }),
      );
      break;
    case 'workflow': // two linked boxes — one step's output is the next step's input
      svg.append(
        child('rect', { width: '8', height: '8', x: '3', y: '3', rx: '2' }),
        child('path', { d: 'M7 11v4a2 2 0 0 0 2 2h4' }),
        child('rect', { width: '8', height: '8', x: '13', y: '13', rx: '2' }),
      );
      break;
    case 'file-cog': // a document with a gear — the settings a written-down thing is governed by
      // Drawn here in the idiom rather than transcribed: lucide's own `file-cog` composes its gear
      // from ten separate tooth paths, and a gear assembled out of half-remembered fragments is a
      // worse mark than one drawn deliberately. The document outline IS lucide's, the same one
      // `file-text` and `square-pen` use, so the two read as one family.
      svg.append(
        child('path', { d: 'M14 2v4a2 2 0 0 0 2 2h4' }),
        child('path', { d: 'M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z' }),
        child('circle', { cx: '12', cy: '15', r: '2' }),
        child('path', { d: 'M12 11.5v1' }),
        child('path', { d: 'M12 17.5v1' }),
        child('path', { d: 'M8.75 15h1' }),
        child('path', { d: 'M14.25 15h1' }),
      );
      break;
    case 'bot': // a machine with a face — something that answered, but not a person
      svg.append(
        child('path', { d: 'M12 8V4H8' }),
        child('rect', { width: '16', height: '12', x: '4', y: '8', rx: '2' }),
        child('path', { d: 'M2 14h2' }),
        child('path', { d: 'M20 14h2' }),
        child('path', { d: 'M15 13v2' }),
        child('path', { d: 'M9 13v2' }),
      );
      break;
    case 'plug': // a two-pin plug — a connection to something outside this system
      svg.append(
        child('path', { d: 'M12 22v-5' }),
        child('path', { d: 'M9 8V2' }),
        child('path', { d: 'M15 8V2' }),
        child('path', { d: 'M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z' }),
      );
      break;
    case 'database': // a stack of discs — where the records themselves are kept
      svg.append(
        child('ellipse', { cx: '12', cy: '5', rx: '9', ry: '3' }),
        child('path', { d: 'M3 5V19A9 3 0 0 0 21 19V5' }),
        child('path', { d: 'M3 12A9 3 0 0 0 21 12' }),
      );
      break;
    case 'terminal': // a prompt and a caret — the developer's own way in
      svg.append(
        child('polyline', { points: '4 17 10 11 4 5' }),
        child('line', { x1: '12', x2: '20', y1: '19', y2: '19' }),
      );
      break;
    case 'bug': // a beetle — something went wrong in the machinery, and was written down
      svg.append(
        child('path', { d: 'm8 2 1.88 1.88' }),
        child('path', { d: 'M14.12 3.88 16 2' }),
        child('path', { d: 'M9 7.13v-1a3.003 3.003 0 1 1 6 0v1' }),
        child('path', {
          d: 'M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6',
        }),
        child('path', { d: 'M12 20v-9' }),
        child('path', { d: 'M6.53 9C4.6 8.8 3 7.1 3 5' }),
        child('path', { d: 'M6 13H2' }),
        child('path', { d: 'M6.53 17c-1.93.2-3.53 1.9-3.53 4' }),
        child('path', { d: 'M17.47 9c1.93-.2 3.53-1.9 3.53-4' }),
        child('path', { d: 'M18 13h4' }),
        child('path', { d: 'M17.47 17c1.93.2 3.53 1.9 3.53 4' }),
      );
      break;
    case 'scroll-text': // a scroll with writing on it — the record of what happened, kept
      svg.append(
        child('path', { d: 'M15 12h-5' }),
        child('path', { d: 'M15 8h-5' }),
        child('path', { d: 'M19 17V5a2 2 0 0 0-2-2H4' }),
        child('path', {
          d: 'M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3',
        }),
      );
      break;
    case 'paperclip': // a paperclip — a file attached to something else
      svg.append(
        child('path', {
          d: 'm21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48',
        }),
      );
      break;
    case 'flag': // a flag — a person raised this, and it is waiting to be looked at
      svg.append(
        child('path', { d: 'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z' }),
        child('line', { x1: '4', x2: '4', y1: '22', y2: '15' }),
      );
      break;
  }
  return svg;
}

/**
 * Fill every `data-icon` placeholder under `scope` from this vocabulary (CAOS-602).
 *
 * A component that builds its shadow tree from a markup template cannot call {@link makeIcon} in
 * the middle of a string, and the two that tried put SVG SOURCE in the template instead — which is
 * both a second copy of the artwork and the one construction route the CSP rules out. This is the
 * seam that was missing: the template writes `<span class="glyph" data-icon="log-out"></span>` and
 * this fills it afterwards, through the DOM, from the same `case` every other caller draws.
 *
 * A name this vocabulary does not carry leaves the placeholder empty rather than drawing a blank
 * box, the same way {@link isIconName} lets a name that arrived as data be skipped.
 */
export function drawIcons(scope: ParentNode): void {
  for (const holder of Array.from(scope.querySelectorAll('[data-icon]'))) {
    const name = holder.getAttribute('data-icon') ?? '';
    if (isIconName(name)) holder.replaceChildren(makeIcon(name));
  }
}

/** Map an intent to its glyph (danger and its `error` alias share the glyph). */
export function intentIcon(intent: string): SVGSVGElement {
  switch (intent) {
    case 'success':
      return makeIcon('success');
    case 'warning':
      return makeIcon('warning');
    case 'danger':
    case 'error':
      return makeIcon('danger');
    default:
      return makeIcon('info');
  }
}
